import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ProviderSignupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    license_number: ''
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setTokenError('No signup token provided');
      setIsValidating(false);
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/roadside/provider/validate-token/${token}`);
      setTokenValid(true);
      setTokenError('');
    } catch (error) {
      setTokenError(error.response?.data?.detail || 'Invalid or expired signup link');
    } finally {
      setIsValidating(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.license_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/roadside/provider/signup`,
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          license_number: formData.license_number
        },
        {
          params: { token }
        }
      );

      toast.success('Account created successfully!', {
        description: 'You can now login with your credentials'
      });

      // Redirect to provider login
      setTimeout(() => {
        navigate('/provider/login');
      }, 2000);
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Failed to create account', {
        description: error.response?.data?.detail || 'Please try again'
      });
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md w-full">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Validating signup link...</p>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md w-full">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Signup Link</h2>
          <p className="text-gray-600 mb-6">{tokenError}</p>
          <div className="space-y-2 text-sm text-gray-600 text-left bg-gray-50 p-4 rounded-lg">
            <p>This link may be invalid because:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>It has already been used</li>
              <li>It has expired (24-hour limit)</li>
              <li>The link is incorrect or incomplete</li>
            </ul>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Please contact the administrator for a new signup link.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Become a Service Provider
          </h1>
          <p className="text-gray-600">
            Join Vargas Tire & Auto Repair as a roadside assistance provider
          </p>
        </div>

        {/* Success Notice */}
        <Card className="p-4 mb-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800">
              Your signup link is valid. Complete the form below to create your account.
            </p>
          </div>
        </Card>

        {/* Signup Form */}
        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-base font-semibold">
                Full Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="John Doe"
                className="mt-2"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-base font-semibold">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
                className="mt-2"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-base font-semibold">
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="mt-2"
                required
              />
            </div>

            {/* License Number */}
            <div>
              <Label htmlFor="license" className="text-base font-semibold">
                License Number *
              </Label>
              <Input
                id="license"
                value={formData.license_number}
                onChange={(e) => handleChange('license_number', e.target.value)}
                placeholder="ABC12345"
                className="mt-2"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Your professional mechanic license number
              </p>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-base font-semibold">
                Password *
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Minimum 6 characters"
                className="mt-2"
                required
              />
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword" className="text-base font-semibold">
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Re-enter your password"
                className="mt-2"
                required
              />
            </div>

            {/* Terms Notice */}
            <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
              <p>
                By creating an account, you agree to provide roadside assistance services
                under Vargas Tire & Auto Repair and comply with all service standards.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Provider Account
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Login Link */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Already have an account?{' '}
          <a href="/provider/login" className="text-blue-600 hover:text-blue-700 font-semibold">
            Login here
          </a>
        </p>
      </div>
    </div>
  );
};
